import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import useMessageStream from '../../hooks/useMessageStream';
import useDesktopNotifications from '../../hooks/useDesktopNotifications';
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  CreditCard,
  MessageSquare,
  UserCog,
  BarChart3,
  Settings as SettingsIcon,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  LogOut,
  Menu,
  X,
  Plus,
  Wallet,
  Search,
  CalendarCheck,
  Mail,
  Bell,
  BellOff,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ManualBookingDialog } from '../../components/ManualBookingDialog';
import { PaymentModal } from '../../components/PaymentModal';
import { LiveActivityToaster } from '../../components/LiveActivityToaster';
import { CrmChatDock } from '../../components/chat/CrmChatDock';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 7 grouped nav categories (compact, redesigned in iter-96). Reminders moved
// to Parties (they're about parties); Loyalty moved to Customers; Audit Log +
// User Guide moved to Settings; Packages / Time Slots / Special Offers moved
// to Operations; Reports merged with Operations.
const NAV_GROUPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { to: '/crm', label: 'Overview', end: true },
    ],
  },
  {
    id: 'parties',
    label: 'Parties',
    icon: CalendarDays,
    items: [
      { to: '/crm/bookings', label: 'Bookings' },
      { to: '/crm/waivers', label: 'Liability Waivers', adminOnly: true },
      { to: '/crm/invitations', label: 'Invitations', adminOnly: true },
      { to: '/crm/prospects', label: 'Prospects' },
      { to: '/crm/reminders', label: 'Reminders' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    items: [
      { to: '/crm/customers', label: 'All Customers' },
      { to: '/crm/loyalty', label: 'Loyalty Program', adminOnly: true },
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: CreditCard,
    items: [
      { to: '/crm/payments', label: 'Payments', adminOnly: true },
      { to: '/crm/invoices', label: 'Invoices' },
      { to: '/crm/discounts', label: 'Discounts', adminOnly: true },
      { to: '/crm/gift-cards', label: 'Gift Cards', adminOnly: true },
      { to: '/crm/redeem', label: 'POS · Scan to Redeem' },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: MessageSquare,
    items: [
      { to: '/crm/emails', label: 'Inbox', badgeKey: 'emails' },
      { to: '/crm/customer-messages', label: 'Customer Messages', badgeKey: 'customer_msgs' },
      { to: '/crm/communications', label: 'Email & SMS Templates' },
      { to: '/crm/messages', label: 'Employee Messages' },
      { to: '/crm/support', label: 'Support Tickets' },
    ],
  },
  {
    id: 'staff',
    label: 'Staff',
    icon: UserCog,
    items: [
      { to: '/crm/employees', label: 'Employees', adminOnly: true },
      { to: '/crm/staff/performance', label: 'Performance', adminOnly: true },
      { to: '/crm/shifts', label: 'Shifts' },
      { to: '/crm/shift-swaps', label: 'Shift Swaps', badgeKey: 'pending_swaps' },
      { to: '/crm/reactivation-requests', label: 'Reactivation Requests', badgeKey: 'reactivation' },
      { to: '/crm/job-applications', label: 'Job Applications' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: BarChart3,
    items: [
      { to: '/crm/analytics', label: 'Analytics' },
      { to: '/crm/accounting', label: 'Accounting', adminOnly: true },
      { to: '/crm/accounting/bank-imports', label: 'Bank Imports' },
      { to: '/crm/payroll', label: 'Payroll', adminOnly: true },
      { to: '/crm/products/addons', label: 'Add-on Menu', adminOnly: true },
      { to: '/crm/inventory', label: 'Inventory', badgeKey: 'pending_deductions' },
      { to: '/crm/settings/packages', label: 'Packages', adminOnly: true },
      { to: '/crm/settings/time-slots', label: 'Time Slots', adminOnly: true },
      { to: '/crm/settings/special-offers', label: 'Special Offers', adminOnly: true },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon,
    items: [
      { to: '/crm/settings', label: 'General', adminOnly: true },
      { to: '/crm/settings/landing', label: 'Landing Page', adminOnly: true },
      { to: '/crm/settings/designs', label: 'Page Designs', adminOnly: true },
      { to: '/crm/settings/menu', label: 'Main Menu', adminOnly: true },
      { to: '/crm/hero-testimonials', label: 'Hero Slides', adminOnly: true },
      { to: '/crm/settings/contact', label: 'Contact Information', adminOnly: true },
      { to: '/crm/settings/employer', label: 'Employer & Payroll', adminOnly: true },
      { to: '/crm/audit', label: 'Audit Log', adminOnly: true },
      { to: '/crm/guide', label: 'User Guide' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: ShieldCheck,
    items: [
      { to: '/crm/admin', label: 'Admin Control Center', superAdminOnly: true },
    ],
  },
];

// Maps a nav item's `to` path to a permission/capability section key. Paths not
// listed here (e.g. /crm, /crm/redeem, /crm/guide) fall back to legacy gating.
const SECTION_BY_PATH = {
  '/crm/bookings': 'bookings', '/crm/invitations': 'invitations', '/crm/prospects': 'prospects', '/crm/reminders': 'reminders',
  '/crm/customers': 'customers', '/crm/loyalty': 'loyalty',
  '/crm/payments': 'payments', '/crm/invoices': 'invoices', '/crm/discounts': 'discounts', '/crm/gift-cards': 'gift_cards',
  '/crm/emails': 'emails', '/crm/communications': 'communications', '/crm/messages': 'messages', '/crm/support': 'support',
  '/crm/employees': 'employees', '/crm/staff/performance': 'performance', '/crm/shifts': 'shifts', '/crm/shift-swaps': 'shift_swaps', '/crm/job-applications': 'job_applications',
  '/crm/analytics': 'analytics', '/crm/accounting': 'accounting', '/crm/accounting/bank-imports': 'bank_imports', '/crm/payroll': 'payroll', '/crm/products/addons': 'addons', '/crm/inventory': 'inventory', '/crm/settings/packages': 'packages', '/crm/settings/time-slots': 'time_slots', '/crm/settings/special-offers': 'special_offers',
  '/crm/settings': 'settings', '/crm/settings/landing': 'landing', '/crm/settings/designs': 'landing', '/crm/settings/menu': 'landing', '/crm/hero-testimonials': 'landing', '/crm/settings/contact': 'settings', '/crm/settings/employer': 'settings', '/crm/audit': 'audit',
  '/crm/admin': 'admin_control',
};

// Routes that belong to each group (used for active-state detection & auto-expand)
const groupForPath = (pathname) => {
  for (const g of NAV_GROUPS) {
    for (const item of g.items) {
      if (item.end) {
        if (pathname === item.to) return g.id;
      } else if (pathname === item.to || pathname.startsWith(item.to + '/')) {
        return g.id;
      }
    }
  }
  return 'dashboard';
};

export const CrmLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isSuperAdmin, hasPermission, capabilityEnabled, hasCrmAccess, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => ({ [groupForPath(location.pathname)]: true }));
  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  const [posOpen, setPosOpen] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [unreadEmails, setUnreadEmails] = useState(0);
  const [pendingDeductions, setPendingDeductions] = useState(0);
  const [pendingSwaps, setPendingSwaps] = useState(0);
  const [pendingReactivations, setPendingReactivations] = useState(0);
  const [customerMsgs, setCustomerMsgs] = useState(0);
  // Icon-only collapsed sidebar (desktop). Persisted in localStorage.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('crmSidebarCollapsed') === '1'; } catch (_) { return false; }
  });
  // Sidebar search query
  const [navQuery, setNavQuery] = useState('');

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('crmSidebarCollapsed', next ? '1' : '0'); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  // Redirect non-authorized users
  useEffect(() => {
    if (!loading && !hasCrmAccess) {
      navigate('/');
    }
  }, [loading, hasCrmAccess, navigate]);

  // Keep the group of the active route expanded
  useEffect(() => {
    const active = groupForPath(location.pathname);
    setExpanded((prev) => ({ ...prev, [active]: true }));
  }, [location.pathname]);

  // Fetch unread employee-messages count for the badge
  const fetchUnread = useCallback(async () => {
    if (!hasCrmAccess) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/employee-messages/summary`, { withCredentials: true });
      const total = (res.data || []).reduce((sum, e) => sum + (e.unread || 0), 0);
      setUnreadTotal(total);
    } catch {
      // silent — badge just won't show
    }
  }, [hasCrmAccess]);

  // Fetch unread email count for the Inbox sidebar badge
  const fetchUnreadEmails = useCallback(async () => {
    if (!hasCrmAccess) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/emails/unread-count`, { withCredentials: true });
      setUnreadEmails(Number(res.data?.total) || 0);
    } catch {
      // silent — endpoint may 503 if IMAP not configured
    }
  }, [hasCrmAccess]);

  // Fetch pending inventory deductions count for the Inventory sidebar badge
  const fetchPendingDeductions = useCallback(async () => {
    if (!hasCrmAccess) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/inventory/summary`, { withCredentials: true });
      setPendingDeductions(Number(res.data?.pending_deductions) || 0);
    } catch {
      // silent
    }
  }, [hasCrmAccess]);

  // Fetch pending shift swap requests count for the Shift Swaps sidebar badge
  const fetchPendingSwaps = useCallback(async () => {
    if (!hasCrmAccess) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/shift-swaps/pending-count`, { withCredentials: true });
      setPendingSwaps(Number(res.data?.pending) || 0);
    } catch {
      // silent
    }
  }, [hasCrmAccess]);

  // Fetch pending reactivation requests count for the sidebar badge
  const fetchPendingReactivations = useCallback(async () => {
    if (!hasCrmAccess) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/reactivation-requests/pending-count`, { withCredentials: true });
      setPendingReactivations(Number(res.data?.pending) || 0);
    } catch {
      // silent
    }
  }, [hasCrmAccess]);

  // Fetch unread customer-message count for the Customer Messages badge
  const fetchCustomerMsgs = useCallback(async () => {
    if (!hasCrmAccess) return;
    try {
      const res = await axios.get(`${API_URL}/api/staff/threads/unread-count`, { withCredentials: true });
      setCustomerMsgs(Number(res.data?.unread) || 0);
    } catch {
      // silent
    }
  }, [hasCrmAccess]);

  useEffect(() => {
    fetchUnread();
    fetchUnreadEmails();
    fetchPendingDeductions();
    fetchPendingSwaps();
    fetchPendingReactivations();
    fetchCustomerMsgs();
  }, [fetchUnread, fetchUnreadEmails, fetchPendingDeductions, fetchPendingSwaps, fetchPendingReactivations, fetchCustomerMsgs, location.pathname]);

  // Live-refresh both counters when SSE pushes events
  const desktopNotif = useDesktopNotifications();
  useMessageStream(useCallback((evt) => {
    if (evt?.type === 'new_message') fetchUnread();
    if (evt?.type === 'thread_message' || evt?.type === 'thread_read') fetchCustomerMsgs();
    if (evt?.type === 'shift_swap_requested' || evt?.type === 'shift_swap_resolved') {
      fetchPendingSwaps();
    }
    if (evt?.type === 'new_email') {
      // Server sends the freshly-computed unread total in the payload, so we
      // can update the badge without re-fetching the count endpoint.
      if (typeof evt.unread_total === 'number') {
        setUnreadEmails(evt.unread_total);
      } else {
        fetchUnreadEmails();
      }
      // Surface an OS-level notification when the tab is in the background
      const count = Number(evt.new_count) || 1;
      const mboxes = Object.entries(evt.new_by_mailbox || {})
        .map(([k, v]) => `${k}@ (${v})`).join(', ');
      desktopNotif.notify({
        title: count === 1 ? 'New email — Creative Birthdays' : `${count} new emails — Creative Birthdays`,
        body: mboxes ? `Inbox: ${mboxes}` : 'Click to open the unified inbox',
        tag: 'cb-new-email',
        icon: '/favicon-192.png',
        onClick: () => {
          navigate('/crm/emails');
        },
      });
    }
  }, [fetchUnread, fetchUnreadEmails, fetchPendingSwaps, fetchCustomerMsgs, desktopNotif, navigate]));

  const visibleGroups = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    const canSee = (it) => {
      const section = SECTION_BY_PATH[it.to];
      if (section && section !== 'admin_control' && !capabilityEnabled(section)) return false;
      if (it.superAdminOnly) return isSuperAdmin;
      if (!section) return !it.adminOnly || isAdmin;
      if (isSuperAdmin) return true;
      const perm = hasPermission(section, 'view');
      if (perm === null) return !it.adminOnly || isAdmin;
      return perm;
    };
    return NAV_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(canSee),
    }))
      .map((g) => {
        if (!q) return g;
        const filtered = g.items.filter((it) =>
          it.label.toLowerCase().includes(q) || g.label.toLowerCase().includes(q)
        );
        return { ...g, items: filtered };
      })
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isSuperAdmin, navQuery, user?.permissions, user?.capabilities]);

  // When a search is active, force-expand every matching group
  const searchActive = navQuery.trim().length > 0;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const closeSidebar = () => setSidebarOpen(false);

  const toggleGroup = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const activeGroupId = groupForPath(location.pathname);

  // Quick Actions
  const handleNewBooking = () => setManualBookingOpen(true);
  const handleTakePayment = () => setPosOpen(true);
  const handleFindCustomer = () => navigate('/crm/customers?focus=search');
  const handleTodayParties = () => navigate('/crm');
  const handleUnreadMessages = () => navigate('/crm/messages');

  const SidebarContent = () => (
    <>
      {/* Logo + collapse toggle */}
      <div className={`border-b border-[#E8E5DF] ${sidebarCollapsed ? 'p-3' : 'p-5'}`}>
        <div className="flex items-center justify-between">
          {!sidebarCollapsed && (
            <button onClick={() => navigate('/')} className="flex items-center gap-2 group" data-testid="crm-logo">
              <span className="text-xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                CB
              </span>
              <span className="text-sm font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                CRM
              </span>
            </button>
          )}
          {sidebarCollapsed && (
            <button onClick={() => navigate('/')} className="mx-auto" data-testid="crm-logo-collapsed" title="Creative Birthdays CRM">
              <span className="text-xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka, sans-serif' }}>CB</span>
            </button>
          )}
          <button
            onClick={closeSidebar}
            className="lg:hidden p-2 rounded-lg hover:bg-[#FFFDF8] text-[#6B6B7A]"
            data-testid="close-sidebar"
          >
            <X className="w-5 h-5" />
          </button>
          {!sidebarCollapsed && (
            <button
              onClick={toggleSidebarCollapsed}
              className="hidden lg:inline-flex p-1.5 rounded-lg hover:bg-[#FFFDF8] text-[#6B6B7A]"
              title="Collapse sidebar"
              data-testid="crm-sidebar-collapse-toggle"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
        </div>
        {!sidebarCollapsed && <p className="text-xs text-[#6B6B7A] mt-1">Business Management</p>}
      </div>

      {/* Search */}
      {!sidebarCollapsed && (
        <div className="px-3 pt-3" data-testid="crm-nav-search-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B6B7A]" />
            <input
              type="text"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              placeholder="Search menu…"
              className="w-full pl-8 pr-7 py-2 text-xs rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] focus:outline-none focus:ring-2 focus:ring-[#FF3B6B]/30 focus:border-[#FF3B6B]"
              data-testid="crm-nav-search"
            />
            {navQuery && (
              <button
                onClick={() => setNavQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B6B7A] hover:text-[#FF3B6B]"
                data-testid="crm-nav-search-clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Expand-from-collapsed toggle on the rail */}
      {sidebarCollapsed && (
        <button
          onClick={toggleSidebarCollapsed}
          className="mx-auto mt-2 mb-1 p-1.5 rounded-lg hover:bg-[#FFFDF8] text-[#6B6B7A]"
          title="Expand sidebar"
          data-testid="crm-sidebar-expand-toggle"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}

      {/* Grouped Navigation */}
      <nav className={`flex-1 p-3 space-y-1 overflow-y-auto ${sidebarCollapsed ? 'overflow-x-hidden' : ''}`} data-testid="crm-sidebar-nav">
        {visibleGroups.length === 0 && (
          <p className="px-3 py-2 text-xs text-[#6B6B7A]">No matches.</p>
        )}
        {visibleGroups.map((group) => {
          const GroupIcon = group.icon;
          const isExpanded = searchActive || !!expanded[group.id];
          const isActiveGroup = activeGroupId === group.id;
          const isFlat = group.items.length === 1;

          // ── COLLAPSED ICON-ONLY MODE ──
          if (sidebarCollapsed) {
            const dest = isFlat ? group.items[0].to : (group.items[0]?.to || '#');
            const badgeTotal = group.items.reduce((sum, it) => {
              const c = it.badgeKey === 'emails' ? unreadEmails :
                        it.badgeKey === 'pending_deductions' ? pendingDeductions :
                        it.badgeKey === 'pending_swaps' ? pendingSwaps :
                        it.badgeKey === 'customer_msgs' ? customerMsgs :
                        it.badgeKey === 'reactivation' ? pendingReactivations : 0;
              return sum + c;
            }, 0);
            return (
              <NavLink
                key={group.id}
                to={dest}
                end={isFlat ? group.items[0].end : false}
                onClick={closeSidebar}
                title={group.label}
                className={({ isActive }) =>
                  `relative flex items-center justify-center w-12 h-12 mx-auto rounded-xl transition-all ${
                    isActiveGroup || isActive
                      ? 'bg-[#FF3B6B]/10 text-[#FF3B6B]'
                      : 'text-[#1E1E24] hover:bg-[#FFFDF8]'
                  }`
                }
                data-testid={`crm-nav-group-${group.id}`}
              >
                <GroupIcon className="w-5 h-5" />
                {badgeTotal > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-[#FF3B6B] text-white text-[9px] font-bold">
                    {badgeTotal > 99 ? '99+' : badgeTotal}
                  </span>
                )}
              </NavLink>
            );
          }

          // ── EXPANDED MODE ──
          if (isFlat) {
            const only = group.items[0];
            return (
              <NavLink
                key={group.id}
                to={only.to}
                end={only.end}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-[#FF3B6B]/10 text-[#FF3B6B]'
                      : 'text-[#1E1E24] hover:bg-[#FFFDF8]'
                  }`
                }
                data-testid={`crm-nav-group-${group.id}`}
              >
                <GroupIcon className="w-5 h-5" />
                {group.label}
              </NavLink>
            );
          }
          return (
            <div key={group.id} className="space-y-0.5">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isExpanded}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  isActiveGroup
                    ? 'text-[#FF3B6B]'
                    : 'text-[#1E1E24] hover:bg-[#FFFDF8]'
                }`}
                data-testid={`crm-nav-group-${group.id}`}
              >
                <span className="flex items-center gap-3">
                  <GroupIcon className="w-5 h-5" />
                  {group.label}
                </span>
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 opacity-70" aria-hidden />
                ) : (
                  <ChevronRight className="w-4 h-4 opacity-70" aria-hidden />
                )}
              </button>
              {isExpanded && (
                <div className="ml-8 pl-3 border-l border-[#F0EDE6] space-y-0.5 py-1">
                  {group.items.map((item) => {
                    const badgeCount =
                      item.badgeKey === 'emails' ? unreadEmails :
                      item.badgeKey === 'pending_deductions' ? pendingDeductions :
                      item.badgeKey === 'pending_swaps' ? pendingSwaps :
                      item.badgeKey === 'customer_msgs' ? customerMsgs :
                      item.badgeKey === 'reactivation' ? pendingReactivations :
                      0;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        onClick={closeSidebar}
                        className={({ isActive }) =>
                          `flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                            isActive
                              ? 'bg-[#FF3B6B]/10 text-[#FF3B6B]'
                              : 'text-[#6B6B7A] hover:bg-[#FFFDF8] hover:text-[#1E1E24]'
                          }`
                        }
                        data-testid={`crm-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        <span>{item.label}</span>
                        {badgeCount > 0 && (
                          <span
                            className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-[#FF3B6B] text-white text-[10px] font-bold shadow-sm"
                            data-testid={`crm-nav-badge-${item.badgeKey}`}
                            aria-label={`${badgeCount} unread`}
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className={`border-t border-[#E8E5DF] space-y-2 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
        {!sidebarCollapsed && (
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-[#1E1E24] truncate">{user?.name}</p>
            <p className="text-xs text-[#6B6B7A] truncate">{user?.email}</p>
          </div>
        )}
        {!sidebarCollapsed && <NotificationToggle desktopNotif={desktopNotif} />}
        <div className={`flex ${sidebarCollapsed ? 'flex-col' : 'flex-row'} gap-2`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { navigate('/'); closeSidebar(); }}
            className={`text-[#6B6B7A] hover:text-[#1E1E24] ${sidebarCollapsed ? 'w-full px-0 justify-center' : 'flex-1'}`}
            title="Back to site"
            data-testid="crm-back-site"
          >
            <ArrowLeft className="w-4 h-4" />
            {!sidebarCollapsed && <span className="ml-1">Site</span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className={`text-[#6B6B7A] hover:text-[#FF3B6B] ${sidebarCollapsed ? 'w-full px-0 justify-center' : 'flex-1'}`}
            title="Logout"
            data-testid="crm-logout"
          >
            <LogOut className="w-4 h-4" />
            {!sidebarCollapsed && <span className="ml-1">Logout</span>}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#FFFDF8]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-white border-r border-[#E8E5DF] flex flex-col h-full z-50
          fixed transition-[width,transform] duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="crm-sidebar"
        data-collapsed={sidebarCollapsed ? '1' : '0'}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <main className={`flex-1 min-h-screen transition-[margin] duration-300 overflow-x-hidden ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#E8E5DF] px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl border border-[#E8E5DF] hover:bg-[#FFFDF8]"
            data-testid="open-sidebar"
          >
            <Menu className="w-5 h-5 text-[#1E1E24]" />
          </button>
          <span className="text-lg font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            CB CRM
          </span>
        </div>

        {/* Quick Action Bar */}
        <QuickActionBar
          onNewBooking={handleNewBooking}
          onTakePayment={handleTakePayment}
          onFindCustomer={handleFindCustomer}
          onTodayParties={handleTodayParties}
          onUnreadMessages={handleUnreadMessages}
          unreadTotal={unreadTotal}
        />

        {/* pb-24 gives the badge (fixed bottom:16, height:40) clear breathing room
            on desktop too so it never overlaps the last row / pagination of any page. */}
        <div className="p-4 sm:p-6 lg:p-8 pb-28 lg:pb-24">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom-tab nav — 5 most-used destinations */}
      <MobileBottomTabs
        unreadEmails={unreadEmails}
        unreadMessages={unreadTotal}
        onMore={() => setSidebarOpen(true)}
      />

      {/* Manual Booking Dialog */}
      <ManualBookingDialog
        open={manualBookingOpen}
        onOpenChange={setManualBookingOpen}
        onCreated={() => {
          setManualBookingOpen(false);
          toast.success('Booking created successfully');
        }}
      />

      {/* Standalone walk-in POS — Take Payment quick action */}
      <PaymentModal
        open={posOpen}
        onOpenChange={setPosOpen}
        mode="admin"
        posMode
        title="Take Payment"
      />

      {/* Live collaboration toaster — surfaces a small toast in the corner
          whenever a teammate makes a change anywhere in the CRM. */}
      <LiveActivityToaster />

      {/* Floating customer-chat dock — keep chatting while working anywhere in the CRM */}
      <CrmChatDock />
    </div>
  );
};

// -----------------------------
// Quick Action Bar
// -----------------------------
const QuickActionBar = ({
  onNewBooking,
  onTakePayment,
  onFindCustomer,
  onTodayParties,
  onUnreadMessages,
  unreadTotal,
}) => {
  const actions = [
    { id: 'new-booking', label: 'New Booking', icon: Plus, onClick: onNewBooking, accent: 'bg-[#FF3B6B] hover:bg-[#E6325F] text-white' },
    { id: 'take-payment', label: 'Take Payment', icon: Wallet, onClick: onTakePayment, accent: 'bg-[#06D6A0] hover:bg-[#05B88A] text-white' },
    { id: 'find-customer', label: 'Find Customer', icon: Search, onClick: onFindCustomer, accent: 'bg-white text-[#1E1E24] border border-[#E8E5DF] hover:bg-[#FFFDF8]' },
    { id: 'today-parties', label: "Today's Parties", icon: CalendarCheck, onClick: onTodayParties, accent: 'bg-white text-[#1E1E24] border border-[#E8E5DF] hover:bg-[#FFFDF8]' },
    { id: 'unread-messages', label: 'Unread Messages', icon: Mail, onClick: onUnreadMessages, accent: 'bg-white text-[#1E1E24] border border-[#E8E5DF] hover:bg-[#FFFDF8]', badge: unreadTotal },
  ];

  return (
    <div
      className="sticky top-[52px] lg:top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-[#E8E5DF] px-4 sm:px-6 lg:px-8 py-3"
      data-testid="crm-quick-action-bar"
    >
      <div
        className="flex items-center gap-2 overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        <span className="hidden sm:inline-flex text-[11px] uppercase tracking-wider font-semibold text-[#6B6B7A] mr-1 whitespace-nowrap">
          Quick actions
        </span>
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              type="button"
              onClick={a.onClick}
              className={`relative flex items-center gap-2 px-3.5 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap active:scale-95 ${a.accent}`}
              data-testid={`quick-action-${a.id}`}
            >
              <Icon className="w-4 h-4" />
              {a.label}
              {typeof a.badge === 'number' && a.badge > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 inline-flex items-center justify-center rounded-full bg-[#FF3B6B] text-white text-[10px] font-bold shadow"
                  data-testid={`quick-action-${a.id}-badge`}
                >
                  {a.badge > 99 ? '99+' : a.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CrmLayout;

// -----------------------------
// Mobile bottom-tab navigation (5 most-used destinations)
// -----------------------------
const MobileBottomTabs = ({ unreadEmails, unreadMessages, onMore }) => {
  const tabs = [
    { to: '/crm', end: true, icon: LayoutDashboard, label: 'Home', testid: 'mtab-home' },
    { to: '/crm/bookings', icon: CalendarDays, label: 'Parties', testid: 'mtab-parties' },
    { to: '/crm/customers', icon: Users, label: 'Customers', testid: 'mtab-customers' },
    { to: '/crm/emails', icon: Mail, label: 'Inbox', testid: 'mtab-inbox', badge: unreadEmails },
    { type: 'more', icon: MoreHorizontal, label: 'More', testid: 'mtab-more', badge: unreadMessages },
  ];
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#E8E5DF] flex items-stretch"
      data-testid="crm-mobile-tabs"
    >
      {tabs.map((t) => {
        const Icon = t.icon;
        const inner = (
          <>
            <Icon className="w-5 h-5" />
            <span className="text-[10px] font-semibold mt-0.5">{t.label}</span>
            {t.badge > 0 && (
              <span className="absolute top-0.5 right-1/2 translate-x-4 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-[#FF3B6B] text-white text-[9px] font-bold">
                {t.badge > 99 ? '99+' : t.badge}
              </span>
            )}
          </>
        );
        const baseCls = 'relative flex-1 flex flex-col items-center justify-center py-2 transition-colors';
        if (t.type === 'more') {
          return (
            <button
              key={t.testid}
              onClick={onMore}
              className={`${baseCls} text-[#6B6B7A] hover:text-[#FF3B6B]`}
              data-testid={t.testid}
            >
              {inner}
            </button>
          );
        }
        return (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              `${baseCls} ${isActive ? 'text-[#FF3B6B]' : 'text-[#6B6B7A] hover:text-[#1E1E24]'}`
            }
            data-testid={t.testid}
          >
            {inner}
          </NavLink>
        );
      })}
    </nav>
  );
};

// -----------------------------
// Sidebar footer: desktop notifications toggle
// -----------------------------
const NotificationToggle = ({ desktopNotif }) => {
  if (!desktopNotif?.supported) return null;
  const { permission, request, notify } = desktopNotif;

  const onClick = async () => {
    if (permission === 'granted') {
      // Already on — fire a tiny test notification so the user sees it works.
      notify({
        title: 'Notifications are on',
        body: "You'll be alerted when new customer emails arrive.",
        tag: 'cb-notif-test',
        icon: '/favicon-192.png',
        force: true,
        autoCloseMs: 4000,
      });
      try { (await import('sonner')).toast.success('Test notification sent — check your OS tray'); } catch { /* noop */ }
      return;
    }
    if (permission === 'denied') {
      try { (await import('sonner')).toast.info('Notifications are blocked. Enable them from your browser site settings.'); } catch { /* noop */ }
      return;
    }
    const result = await request();
    if (result === 'granted') {
      try { (await import('sonner')).toast.success('Desktop notifications enabled'); } catch { /* noop */ }
    }
  };

  const isOn = permission === 'granted';
  const isBlocked = permission === 'denied';
  const Icon = isOn ? Bell : BellOff;
  const label = isOn ? 'Notifications on' : isBlocked ? 'Notifications blocked' : 'Enable notifications';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
        isOn
          ? 'bg-[#06D6A0]/10 text-[#06D6A0] hover:bg-[#06D6A0]/15'
          : isBlocked
            ? 'bg-[#F0EDE6] text-[#9a9aa6] cursor-help'
            : 'bg-white border border-[#E8E5DF] text-[#1E1E24] hover:bg-[#FFFDF8]'
      }`}
      data-testid="crm-notif-toggle"
      title={isBlocked ? 'Unblock notifications in browser site settings' : ''}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
};
